// New Ticket Dialog - Create tickets on behalf of customers or internal
import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  TextField,
  Button,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  IconButton,
  Divider,
  Grid,
} from '@mui/material';
import {
  Close,
  Person,
  Business,
  BugReport,
  Help,
  Feedback,
  Lightbulb,
  QuestionAnswer,
} from '@mui/icons-material';
import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useOrganization } from '@/contexts/OrganizationContext';
import { useAuth } from '@/contexts/AuthContext';
import { useToast } from '@/contexts/ToastContext';
import { ticketsService } from '@/services/collections/tickets';
import type { TicketType, TicketPriority } from '@/types';

const ticketSchema = z.object({
  source: z.enum(['customer', 'internal']),
  submitterEmail: z.string().email('Valid email required').optional().or(z.literal('')),
  submitterName: z.string().optional(),
  type: z.enum(['bug', 'feedback', 'feature_request', 'support', 'question']),
  priority: z.enum(['low', 'medium', 'high', 'urgent']),
  subject: z.string().min(3, 'Subject must be at least 3 characters'),
  description: z.string().min(10, 'Description must be at least 10 characters'),
}).refine((data) => {
  // If customer source, email is required
  if (data.source === 'customer' && !data.submitterEmail) {
    return false;
  }
  return true;
}, {
  message: 'Customer email is required',
  path: ['submitterEmail'],
});

type TicketFormData = z.infer<typeof ticketSchema>;

interface NewTicketDialogProps {
  open: boolean;
  onClose: () => void;
  onCreated?: () => void;
}

const TYPE_OPTIONS: { value: TicketType; label: string; icon: React.ReactNode; color: string }[] = [
  { value: 'bug', label: 'Bug Report', icon: <BugReport />, color: '#ef4444' },
  { value: 'support', label: 'Support', icon: <Help />, color: '#3b82f6' },
  { value: 'feedback', label: 'Feedback', icon: <Feedback />, color: '#8b5cf6' },
  { value: 'feature_request', label: 'Feature Request', icon: <Lightbulb />, color: '#f59e0b' },
  { value: 'question', label: 'Question', icon: <QuestionAnswer />, color: '#10b981' },
];

const PRIORITY_OPTIONS: { value: TicketPriority; label: string; color: string }[] = [
  { value: 'low', label: 'Low', color: '#94a3b8' },
  { value: 'medium', label: 'Medium', color: '#3b82f6' },
  { value: 'high', label: 'High', color: '#f59e0b' },
  { value: 'urgent', label: 'Urgent', color: '#ef4444' },
];

export const NewTicketDialog = ({ open, onClose, onCreated }: NewTicketDialogProps) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('md'));
  const { organization, project } = useOrganization();
  const { user, profile } = useAuth();
  const { showSuccess, showError } = useToast();
  const [submitting, setSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    control,
    watch,
    reset,
    formState: { errors },
  } = useForm<TicketFormData>({
    resolver: zodResolver(ticketSchema),
    defaultValues: {
      source: 'customer',
      submitterEmail: '',
      submitterName: '',
      type: 'support',
      priority: 'medium',
      subject: '',
      description: '',
    },
  });

  const source = watch('source');

  const handleClose = () => {
    reset();
    onClose();
  };

  const onSubmit = async (data: TicketFormData) => {
    if (!organization || !project || !user) return;

    setSubmitting(true);
    try {
      // Generate ticket number (simple increment - in production use a counter)
      const ticketNumber = Date.now() % 100000;

      const ticketData = {
        organizationId: organization.id,
        projectId: project.id,
        ticketNumber,
        type: data.type as TicketType,
        status: 'new' as const,
        priority: data.priority as TicketPriority,
        subject: data.subject,
        description: data.description,
        submitterEmail: data.source === 'customer' ? data.submitterEmail! : user.email || '',
        submitterName: data.source === 'customer' ? data.submitterName : profile?.displayName || 'Internal',
        // Mark internal tickets
        tags: data.source === 'internal' ? ['internal'] : [],
        // Auto-assign internal tickets to creator
        assigneeId: data.source === 'internal' ? user.uid : undefined,
        assigneeName: data.source === 'internal' ? profile?.displayName : undefined,
        screenshots: [],
        attachments: [],
        internalNotes: data.source === 'internal' ? 'Created as internal ticket' : undefined,
        createdBy: user.uid,
      };

      await ticketsService.create(ticketData, user.uid);

      showSuccess('Ticket created successfully!');
      handleClose();
      onCreated?.();
    } catch (err) {
      console.error('Error creating ticket:', err);
      showError('Failed to create ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      fullScreen={fullScreen}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: fullScreen ? 0 : 3,
          maxHeight: fullScreen ? '100%' : '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          py: 1.5,
          px: 2,
        }}
      >
        <Box>
          <Typography variant="h6" fontWeight="bold">
            Create New Ticket
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {source === 'customer' ? 'On behalf of a customer' : 'Internal support task'}
          </Typography>
        </Box>
        <IconButton onClick={handleClose} edge="end" size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent sx={{ pt: 2, pb: 1 }}>
        <Box component="form" id="new-ticket-form" onSubmit={handleSubmit(onSubmit)}>
          {/* Source Toggle */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 0.75 }}>
              Ticket Source
            </Typography>
            <Controller
              name="source"
              control={control}
              render={({ field }) => (
                <ToggleButtonGroup
                  {...field}
                  exclusive
                  fullWidth
                  size="small"
                  sx={{
                    '& .MuiToggleButton-root': {
                      py: 0.75,
                      textTransform: 'none',
                      '&.Mui-selected': {
                        bgcolor: 'primary.main',
                        color: 'white',
                        '&:hover': {
                          bgcolor: 'primary.dark',
                        },
                      },
                    },
                  }}
                >
                  <ToggleButton value="customer">
                    <Person sx={{ mr: 0.75, fontSize: 20 }} />
                    Customer Request
                  </ToggleButton>
                  <ToggleButton value="internal">
                    <Business sx={{ mr: 0.75, fontSize: 20 }} />
                    Internal Task
                  </ToggleButton>
                </ToggleButtonGroup>
              )}
            />
          </Box>

          {/* Customer Info (only for customer source) */}
          {source === 'customer' && (
            <Box
              sx={{
                p: 1.5,
                mb: 2,
                borderRadius: 1.5,
                bgcolor: 'grey.50',
                border: '1px solid',
                borderColor: 'grey.200',
              }}
            >
              <Grid container spacing={1.5}>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    {...register('submitterEmail')}
                    label="Customer Email"
                    type="email"
                    fullWidth
                    required
                    size="small"
                    error={!!errors.submitterEmail}
                    helperText={errors.submitterEmail?.message}
                    placeholder="customer@example.com"
                  />
                </Grid>
                <Grid size={{ xs: 12, sm: 6 }}>
                  <TextField
                    {...register('submitterName')}
                    label="Customer Name"
                    fullWidth
                    size="small"
                    placeholder="John Doe (optional)"
                  />
                </Grid>
              </Grid>
            </Box>
          )}

          {source === 'internal' && (
            <Alert severity="info" sx={{ mb: 2, py: 0.5 }}>
              Internal tickets are automatically assigned to you and tagged as "internal".
            </Alert>
          )}

          {/* Type Selection */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
              Ticket Type
            </Typography>
            <Controller
              name="type"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {TYPE_OPTIONS.map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => field.onChange(option.value)}
                      sx={{
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 1.5,
                        border: '1.5px solid',
                        borderColor: field.value === option.value ? option.color : 'grey.300',
                        bgcolor: field.value === option.value ? `${option.color}12` : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                          borderColor: option.color,
                          bgcolor: `${option.color}08`,
                        },
                      }}
                    >
                      <Box sx={{ color: option.color, display: 'flex', '& svg': { fontSize: 18 } }}>{option.icon}</Box>
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8125rem' }}>
                        {option.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            />
          </Box>

          {/* Priority Selection */}
          <Box sx={{ mb: 2.5 }}>
            <Typography variant="subtitle2" fontWeight="medium" sx={{ mb: 1 }}>
              Priority
            </Typography>
            <Controller
              name="priority"
              control={control}
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
                  {PRIORITY_OPTIONS.map((option) => (
                    <Box
                      key={option.value}
                      onClick={() => field.onChange(option.value)}
                      sx={{
                        px: 1.25,
                        py: 0.5,
                        borderRadius: 1.5,
                        border: '1.5px solid',
                        borderColor: field.value === option.value ? option.color : 'grey.300',
                        bgcolor: field.value === option.value ? `${option.color}15` : 'transparent',
                        cursor: 'pointer',
                        transition: 'all 0.15s',
                        display: 'flex',
                        alignItems: 'center',
                        gap: 0.5,
                        '&:hover': {
                          borderColor: option.color,
                        },
                      }}
                    >
                      <Box
                        sx={{
                          width: 8,
                          height: 8,
                          borderRadius: '50%',
                          bgcolor: option.color,
                        }}
                      />
                      <Typography variant="body2" fontWeight="medium" sx={{ fontSize: '0.8125rem' }}>
                        {option.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              )}
            />
          </Box>

          {/* Subject */}
          <TextField
            {...register('subject')}
            label="Subject"
            fullWidth
            required
            size="small"
            error={!!errors.subject}
            helperText={errors.subject?.message}
            placeholder="Brief summary of the issue"
            sx={{ mb: 2 }}
          />

          {/* Description */}
          <TextField
            {...register('description')}
            label="Description"
            fullWidth
            required
            multiline
            rows={3}
            size="small"
            error={!!errors.description}
            helperText={errors.description?.message}
            placeholder="Provide detailed information about the issue or request..."
          />
        </Box>
      </DialogContent>

      <Divider />

      <DialogActions sx={{ px: 2, py: 1.5, gap: 1 }}>
        <Button onClick={handleClose} variant="outlined" size="small" disabled={submitting}>
          Cancel
        </Button>
        <Button
          type="submit"
          form="new-ticket-form"
          variant="contained"
          size="small"
          disabled={submitting}
          startIcon={submitting ? <CircularProgress size={16} /> : null}
          sx={{
            minWidth: 120,
            background: 'linear-gradient(135deg, #3b82f6 0%, #8b5cf6 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
            },
          }}
        >
          {submitting ? 'Creating...' : 'Create Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
